import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import liveApi from '../services/liveApi'

const SkinCarousel = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isInitializedRef = useRef(false)
  const timerRef = useRef(null)
  const liveWinsRef = useRef([])
  const currentIndexRef = useRef(0)
  const [liveWins, setLiveWins] = useState([])
  const [displayedWins, setDisplayedWins] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const [animatingItems, setAnimatingItems] = useState(new Set())

  // Utility function to shuffle array
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }, [])

  // Load live wins data
  const loadLiveWins = useCallback(async () => {
    if (isInitializedRef.current) {
      return
    }
    
    isInitializedRef.current = true
    
    try {
      setIsLoading(true)
      setError(false)
      const data = await liveApi.getLiveWins()
      
      if (data.wins && data.wins.length > 0) {
        setLiveWins(data.wins)
        liveWinsRef.current = data.wins
        // Initialize with first 10 items
        const initialWins = data.wins.slice(0, 10)
        setDisplayedWins(initialWins)
        setCurrentIndex(10)
        currentIndexRef.current = 10
      } else {
        throw new Error('No live wins data available')
      }
    } catch (error) {
      console.error('Failed to load live wins:', error)
      setError(true)
      toast.error('Не удалось загрузить данные о выигрышах')
      isInitializedRef.current = false // Reset on error
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Add new win to the beginning (left side) of displayed wins
  const addNewWin = useCallback(() => {
    if (liveWinsRef.current.length === 0) return

    setDisplayedWins(prev => {
      // Mark all current items as animating (they will slide right)
      setAnimatingItems(new Set(prev.map((_, index) => index)))

      let nextWin
      
      if (currentIndexRef.current < liveWinsRef.current.length) {
        // Use next win from original array
        nextWin = liveWinsRef.current[currentIndexRef.current]
        currentIndexRef.current += 1
        setCurrentIndex(currentIndexRef.current)
      } else {
        // Cycle through shuffled array
        const shuffled = shuffleArray(liveWinsRef.current)
        nextWin = shuffled[0]
        liveWinsRef.current = shuffled
        setLiveWins(shuffled)
        currentIndexRef.current = 1
        setCurrentIndex(1)
      }

      // Add to beginning (left side) and limit to 100 items max
      const newDisplayed = [{ ...nextWin, id: Date.now() + Math.random() }, ...prev]
      return newDisplayed.length > 100 ? newDisplayed.slice(0, 100) : newDisplayed
    })

    // Clear animating items after animation completes
    setTimeout(() => {
      setAnimatingItems(new Set())
    }, 600) // Match animation duration
  }, [shuffleArray])

  // Handle card click - navigate to source
  const handleCardClick = useCallback((win) => {
    if (win.game === 'case' && win.case_id) {
      navigate(`/cases/${win.case_id}`)
    } else if (win.game === 'casebattle') {
      navigate('/battles')
    } else if (win.game === 'classic') {
      navigate('/royale')
    } else if (win.game === 'upgrader') {
      navigate('/upgrader')
    } else if (win.game === 'contract') {
      navigate('/contract')
    }
  }, [navigate])

  // Start timer function without dependencies
  const startTimer = useCallback(() => {
    const scheduleNext = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      
      const randomDelay = Math.random() * 3000 + 1000 // 1-4 seconds
      timerRef.current = setTimeout(() => {
        addNewWin()
        scheduleNext() // Schedule next
      }, randomDelay)
    }
    
    scheduleNext()
  }, []) // No dependencies

  // Setup realtime intervals - only run once when data is loaded
  useEffect(() => {
    if (liveWins.length === 0 || error) return

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    startTimer()

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [liveWins.length > 0, error, startTimer]) // Include startTimer but it has no deps

  // Initialize data on component mount
  useEffect(() => {
    loadLiveWins()
  }, [])

  // Don't render if error occurred
  if (error) {
    return null
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="overflow-hidden py-4 relative">
        <div className="flex gap-4 animate-pulse">
          {Array.from({ length: 10 }).map((_, index) => (
            <div 
              key={index}
              className="flex-shrink-0 w-28 h-20 bg-surface rounded-lg border border-white border-opacity-10"
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden py-4 relative">
      <div className="flex gap-4 pl-4">
        {displayedWins.map((win, index) => (
          <div 
            key={win.id || index}
            onClick={() => handleCardClick(win)}
            className={`flex-shrink-0 w-28 h-20 bg-surface rounded-lg border border-white border-opacity-10 overflow-hidden relative group cursor-pointer ${
              index === 0 ? 'animate-slideInLeft' : 
              animatingItems.has(index) ? 'animate-slideRight' : ''
            }`}
          >
            <img 
              src={win.img} 
              alt={win.name} 
              className="w-full h-full object-contain p-2"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none'
              }}
            />
            {/* Avatar overlay - top left */}
            {win.ava && (
              <div className="absolute top-1 left-1 w-6 h-6 rounded-full overflow-hidden border border-white border-opacity-30 bg-dark-primary">
                <img 
                  src={win.ava} 
                  alt={win.who || 'User'} 
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none'
                  }}
                />
              </div>
            )}
            {/* Profit overlay - top right */}
            <div className={`absolute top-1 right-1 text-xs font-bold px-1 py-0.5 rounded opacity-90 ${
              win.profit && win.profit.startsWith('+') 
                ? 'bg-accent-primary text-dark-primary' 
                : 'bg-gray-600 text-gray-300'
            }`}>
              {win.profit}
            </div>
            {/* Source overlay - bottom left */}
            <div className="absolute bottom-1 left-1 text-[10px] font-normal px-1 py-0.5 rounded text-white/50 max-w-[90px] truncate">
              {win.game === 'case' && win.case_name ? win.case_name : 
               win.game === 'classic' ? 'Battle Royale' :
               win.game === 'upgrader' ? t('games.upgrader') : 
               win.game === 'contract' ? t('games.contracts') : 
               win.game === 'casebattle' ? t('battle.title') :
               t('games.cases')}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default SkinCarousel
